<script lang="ts">
  import type { Snippet } from 'svelte'

  import Icon from './Icon.svelte'

  /**
   * One group of the layer menu, as round 2 draws it (Stage 10.5, 2a): a
   * header that collapses the group, the count of its layers that are on as a
   * field tag, an optional note in a surface box, then the rows.
   *
   * The Models group is one of these too, with the picker as its rows, so the
   * seven groups read as one list.
   */

  interface Props {
    name: string
    /** How many of the group's layers are on; the tag is drawn when above 0. */
    count: number
    /** Only one of the group's layers can be on; says so beside the name. */
    exclusive?: boolean
    /** Why a layer here would turn another one off, when it would. */
    note?: string
    open: boolean
    children: Snippet
  }

  let { name, count, exclusive = false, note = '', open = $bindable(), children }: Props = $props()

  const uid = $props.id()
  const bodyId = `${uid}-body`
</script>

<section class="group">
  <h3>
    <button
      type="button"
      class="head touch-target"
      aria-expanded={open}
      aria-controls={bodyId}
      onclick={() => (open = !open)}
    >
      <span class="name eyebrow">{name}</span>
      {#if exclusive}<span class="hint">One at a time</span>{/if}
      {#if count > 0}<span class="count">{count} on</span>{/if}
      <Icon name={open ? 'chevron-up' : 'chevron-down'} size={16} strokeWidth={2.5} />
    </button>
  </h3>
  {#if note}
    <p class="note">
      <Icon name="info" size={14} strokeWidth={2.2} />
      <span>{note}</span>
    </p>
  {/if}
  <div class="body" id={bodyId} hidden={!open}>
    {#if open}{@render children()}{/if}
  </div>
</section>

<style>
  .group {
    border-bottom: 2px solid var(--color-divider);
  }

  h3 {
    margin: 0;
    font: inherit;
  }

  .head {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    height: var(--hd, 40px);
    padding: 0 var(--px, 16px);
    border: 0;
    background: none;
    cursor: pointer;
    text-align: left;
  }

  .head:hover {
    background: color-mix(in srgb, var(--color-text) 7%, transparent);
  }

  .head:focus-visible {
    outline-offset: -2px;
  }

  .name {
    flex: 1;
    min-width: 0;
  }

  .hint {
    font-size: 11px;
    color: var(--color-muted);
  }

  .count {
    padding: 1px 6px;
    background: var(--color-field);
    color: var(--color-on-field);
    font-size: 11px;
    font-weight: 800;
    white-space: nowrap;
  }

  .note {
    display: flex;
    gap: 8px;
    margin: 0 var(--px, 16px) 10px;
    padding: 8px 10px;
    background: var(--color-surface);
    font-size: 12px;
    line-height: 1.4;
  }

  .note :global(.icon) {
    margin-top: 2px;
  }

  .body {
    padding-bottom: 8px;
  }
</style>
